import { createSlice } from '@reduxjs/toolkit';
import { IOrder } from 'Shared/Types/order';
import {
  getOrderList,
  getPreviousOrders,
  getOrder,
  createOrder,
  refuseOrder,
} from './reducer';
import { RootState } from 'Redux/store';

interface IOrderState {
  current?: IOrder | null;
  list: IOrder[];
  previous: IOrder[];
  count: number;
  error?: string | null;
  loading: boolean;
}

const initialState: IOrderState = {
  current: null,
  list: [],
  previous: [],
  count: 0,
  error: null,
  loading: false,
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    resetCurrentOrders: (state) => {
      state.current = null;
      state.list = [];
    },
    resetPreviousOrders: (state) => {
      state.previous = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOrderList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getOrderList.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(getOrderList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      const { count, results } = action.payload;
      state.count = count;
      state.list = results || [];
    });

    builder.addCase(getPreviousOrders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getPreviousOrders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(getPreviousOrders.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.previous = action.payload || [];
    });

    builder.addCase(getOrder.pending, (state, action) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(getOrder.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.current = action.payload;
    });

    builder.addCase(createOrder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(createOrder.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;

      if (
        Boolean(state.list.length) &&
        state.list[0].order_status.toLowerCase().includes('pending')
      ) {
        state.list = [action.payload, ...(state.list || [])];
      }
    });

    builder.addCase(refuseOrder.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(refuseOrder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });
    builder.addCase(refuseOrder.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
  },
});

export const { resetCurrentOrders } = orderSlice.actions;
export const { resetPreviousOrders } = orderSlice.actions;

export const selectOrderLoading = (state: RootState) => state.order.loading;
export const selectOrderError = (state: RootState) => state.order.error;
export const selectOrderList = (state: RootState) => state.order.list;
export const selectPreviousOrders = (state: RootState) => state.order.previous;
export const selectOrderCount = (state: RootState) => state.order.count;
export const selectCurrentOrder = (state: RootState) => state.order.current;

export default orderSlice;
